import React, { useEffect, useRef, useState } from "react";
import { initHighchartsModules } from "../../../../../utils/initHighchartsModules";
import {
  Button,
  Card,
  Col,
  Dropdown,
  DropdownButton,
  Row,
} from "react-bootstrap";
import {
  fetchInvoiceDistributionByRoleData,
  fetchInvoiceTrendsData,
} from "../../../../../services/BillingDashboardAPI";
import AccordionComponent from "../../../../../common/Accordion";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import YearDropdown from "../../../../../common/YearDropdown";
import {
  getTranscriberDoctorWise,
  getTranscriberWorkbreakdown,
} from "../../../../../services/TranscriberDashboardAPI";
import TopLimitDropdown from "../../../../../common/TopLimitDropdown";
import MonthIntervalDropdown from "../../../../../common/MonthIntervalDropdown";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTH_MAP: Record<string, string> = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12",
};

interface TranscriberDoctorWiseProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
}

const TranscriberDoctorWise: React.FC<TranscriberDoctorWiseProps> = ({
  openDropdown,
  setOpenDropdown,
}) => {
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [selectedYearMonth, setSelectedYearMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  const [topCount, setTopCount] = useState<number>(5);
  const [monthInterval, setMonthInterval] = useState<string>("1");
  const [topLabel, setTopLabel] = useState<string>("Top 5");
  const [intervalLabel, setIntervalLabel] = useState<string>("This Month");
  const [selectedDoctor, setSelectedDoctor] = useState<{
    name: string;
    id: number;
  } | null>(null);

  const [doctorWiseData, setDoctorWiseData] = useState<any[]>([]);
  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});

  const [distributionData, setDistributionData] = useState<any[]>([]);

  useEffect(() => {
    initHighchartsModules();
  }, []);

  const fetchDoctorData = async () => {
    try {
      const result = await getTranscriberDoctorWise(topCount, monthInterval);
      setDoctorWiseData(result);
    } catch (error) {
      console.error("Failed to load case overview data", error);
    }
  };

  useEffect(() => {
    fetchDoctorData();
  }, [topCount, monthInterval]);

  useEffect(() => {
    if (doctorWiseData?.length > 0 && !selectedDoctor) {
      const firstDoctor = doctorWiseData[0];

      setSelectedDoctor({
        name: firstDoctor.qmeDoctorUserFullName,
        id: firstDoctor.qmeDoctorUserId,
      });
    }
  }, [doctorWiseData]);

  const fetchDistributionData = async () => {
    if (!selectedDoctor) return;

    try {
      const result = await getTranscriberWorkbreakdown(
        selectedDoctor.id,
        monthInterval,
      );

      setDistributionData(result);
    } catch (error) {
      console.error("Failed to load case overview data", error);
    }
  };

  useEffect(() => {
    fetchDistributionData();
  }, [selectedDoctor, monthInterval]);

  const seriesData: number[] = doctorWiseData.map((doc: any) =>
    Number(doc.completed || 0),
  );

  const prepareTrendChart = () => {
    if (!doctorWiseData || doctorWiseData.length === 0) return;

    const categories = doctorWiseData.map(
      (doc: any) => doc.qmeDoctorUserFullName,
    );

    const seriesData = doctorWiseData.map((doc: any) =>
      Number(doc.completed || 0),
    );

    setChartOptions({
      chart: {
        type: "column",
        height: 300,
      },

      title: { text: undefined },

      xAxis: {
        categories,
        labels: {
          style: { fontSize: "12px" },
          rotation: 0, // helps if names are long
        },
      },

      yAxis: {
        min: 0,
        title: { text: "" },
      },

      tooltip: {
        formatter: function () {
          return `<b>${this.x}</b>: ${this.y}`;
        },
      },

      plotOptions: {
        column: {
          color: "#1f9d73",
          dataLabels: {
            enabled: true,
          },
          cursor: "pointer",
          point: {
            events: {
              click: function () {
                const index = this.index;

                const selected = doctorWiseData[index];

                setSelectedDoctor({
                  name: selected.qmeDoctorUserFullName,
                  id: selected.qmeDoctorUserId,
                });
              },
            },
          },
        },
      },

      exporting: { enabled: false },

      credits: { enabled: false },
      legend: { enabled: false },

      series: [
        {
          name: "Completed",
          type: "column",
          data: seriesData,
        },
      ],
    });
  };

  useEffect(() => {
    if (doctorWiseData) {
      prepareTrendChart();
    }
  }, [doctorWiseData]);

  const parsedDistributionData = distributionData?.[0]?.statusCounts
    ? JSON.parse(distributionData[0].statusCounts)
    : {};

  const buildDonutChartOptions = (): Highcharts.Options => {
    if (!parsedDistributionData) return {};

    const data = Object.keys(parsedDistributionData).map((key) => ({
      name: key,
      y: Number(parsedDistributionData[key] || 0),
    }));

    return {
      chart: {
        type: "pie",
        height: 300,
      },

      title: {
        text: selectedDoctor?.name || "",
        style: {
          fontSize: "14px",
        },
      },

      plotOptions: {
        pie: {
          innerSize: "60%",
          dataLabels: {
            enabled: true,
            formatter: function (this: Highcharts.Point) {
              const y = this.y ?? 0;

              return y > 0 ? `${this.name} (${y})` : null;
            },
          },
        },
      },

      tooltip: {
        pointFormat: "<b>{point.name}</b>: {point.y}",
      },

      legend: {
        layout: "horizontal",
        align: "center",
        verticalAlign: "bottom",
      },

      series: [
        {
          type: "pie",
          name: "Workload",
          data,
        },
      ],

      credits: { enabled: false },
      exporting: { enabled: false },
    };
  };

  const handleTopChange = (value: number, label: string) => {
    setTopCount(value);
    setTopLabel(label);
  };

  const handleIntervalChange = (value: string | number, label: string) => {
    setMonthInterval(String(value));
    setIntervalLabel(label);
  };

  const [invoiceKey, setInvoiceKey] = useState<string | null>("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    if (invoiceKey === key) {
      return;
    }
    setInvoiceKey(key || "0");
  };

  const summaryAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={invoiceKey}
      onSelect={handleBodyAccordionSelect}
    />
  );

  const hasAnyData = seriesData.some((val) => val > 0);

  const hasDistributionData = Object.values(parsedDistributionData).some(
    (val: any) => Number(val) > 0,
  );

  return (
    <>
      {summaryAccordion([
        {
          title: (
            <div className="d-flex align-items-center justify-content-between w-100 p-0">
              {/* Left */}
              <span>Transcriptions Completed - Doctor wise</span>

              {/* Center */}
              <span className="text-center flex-grow-1">
                Workload Breakdown
              </span>

              {/* Right */}
              <div className="d-flex gap-2">
                <TopLimitDropdown
                  title={topLabel}
                  onSelect={handleTopChange}
                  show={openDropdown === "transcriberWiseSummary"}
                  onToggle={(isOpen) =>
                    setOpenDropdown(isOpen ? "transcriberWiseSummary" : null)
                  }
                />

                <MonthIntervalDropdown
                  title={intervalLabel}
                  onSelect={handleIntervalChange}
                  show={openDropdown === "transcriberWiseInterval"}
                  onToggle={(isOpen) =>
                    setOpenDropdown(isOpen ? "transcriberWiseInterval" : null)
                  }
                />
              </div>
            </div>
          ),
          content: (
            <>
              <Row className="g-3">
                {/* LEFT CHART */}
                <Col md={6}>
                  {/* Header row for dropdown */}
                  <div
                    className="d-flex justify-content-end align-items-center mb-2"
                    style={{ position: "relative" }}
                  ></div>

                  {/* Chart */}
                  {hasAnyData ? (
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={chartOptions}
                    />
                  ) : (
                    <div className="nodata_wrapper">
                      <span className="material-symbols-outlined">
                        bar_chart_off
                      </span>
                      No Data Available
                    </div>
                  )}
                </Col>

                {/* RIGHT CHART */}
                <Col md={6}>
                  <>
                    {hasDistributionData ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={buildDonutChartOptions()}
                      />
                    ) : (
                      <div className="nodata_wrapper">
                        <span className="material-symbols-outlined">
                          bar_chart_off
                        </span>
                        No Data Available
                      </div>
                    )}
                  </>
                </Col>
              </Row>
            </>
          ),
        },
      ])}
    </>
  );
};

export default TranscriberDoctorWise;
